import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReferralResponse } from '@kiao-voice/api-client'
import { useWorkspaceScopedLoader } from './use-workspace-scoped-loader'

export interface ReferralState {
  /** Latest referral payload, or null before the first probe settles / when
   *  the probe failed. Consumers self-hide when `referral.enabled` is false. */
  referral: ReferralResponse | null
  /** True until the first probe settles (resolved or failed). */
  loading: boolean
  /** Re-fetch on demand (e.g. after the user copies the link, or a manual
   *  refresh). */
  refresh: () => Promise<void>
}

export interface UseReferralOptions {
  enabled: boolean
  /**
   * Injected transport that fetches `GET /api/v1/referral`. Each app supplies
   * its own — desktop passes `() => getReferral(desktopApiClient)`, web passes
   * its token-based fetch — which keeps this hook platform-agnostic.
   *
   * MUST be referentially stable (wrap in `useCallback`); the mount probe keys
   * off it, so an unstable fetcher would re-probe on every render.
   */
  fetchReferral: () => Promise<ReferralResponse>
}

/**
 * Shared referral-stats probe for desktop + web. Fetches `GET /api/v1/referral`
 * once on mount and again whenever the active workspace changes. Referral codes,
 * stats, and rewards belong to the workspace that produced the link, so keeping
 * a previous workspace payload visible would expose the wrong balance and code.
 *
 * Fail-soft: a probe error is logged and leaves `referral` as null so the
 * consumer hides the surface rather than rendering a broken panel — matching
 * the server-flag-off behaviour (no silent catch: it `console.warn`s).
 * Disabled (and cleared) while unauthenticated.
 */
export function useReferral({ enabled, fetchReferral }: UseReferralOptions): ReferralState {
  const [referral, setReferral] = useState<ReferralResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(enabled)
  // Monotonic request id so a slow in-flight probe can't overwrite a newer
  // one's result, and a response that resolves after disable is dropped.
  const requestSeqRef = useRef(0)

  const probe = useCallback(async () => {
    if (!enabled) {
      return
    }
    const seq = ++requestSeqRef.current
    setLoading(true)
    try {
      const next = await fetchReferral()
      if (seq === requestSeqRef.current) {
        setReferral(next)
      }
    } catch (cause) {
      // Fail-soft: hide the surface rather than crash the page. Log so a
      // backend outage is distinguishable from a genuine flag-off.
      console.warn('Referral probe failed', cause)
      if (seq === requestSeqRef.current) {
        setReferral(null)
      }
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false)
      }
    }
  }, [enabled, fetchReferral])

  const clearWorkspace = useCallback(() => {
    requestSeqRef.current += 1
    setReferral(null)
    setLoading(enabled)
  }, [enabled])

  useWorkspaceScopedLoader({
    enabled,
    clear: clearWorkspace,
    load: probe,
  })

  useEffect(() => {
    if (!enabled) {
      // Invalidate any in-flight probe so a late response can't repopulate
      // state for an unauthenticated user.
      requestSeqRef.current++
      setReferral(null)
      setLoading(false)
    }
  }, [enabled])

  return { referral, loading, refresh: probe }
}
